import React from "react";
import { Redirect, Route, Switch, useParams, useRouteMatch } from "react-router-dom";
import { FxGuard } from "@coralblack/flax/dist";
import { VisitEntrance } from "@pages/ecoa/visit/VisitEntrance";
import { VisitQuestionnaire } from "@pages/ecoa/visit/VisitQuestionnaire";
import { VisitQuestionnaireDone } from "@pages/ecoa/visit/VisitQuestionnaireDone";
import { StudySpec } from "maven-lib/dist/consts/edc";
import { ecoaCrfApi } from "maven-lib/dist/states/apis/ecoaCrf";
import { ecoaVisitApi } from "maven-lib/dist/states/apis/ecoaVisit";
import { CaptureContextProvider } from "../CaptureContext";
import { useIntParam } from "@maven-surface/hooks/useRoute";

interface EcoaVisitRouteProps {
  studySpec: StudySpec;
}

interface EcoaVisitRouteInnerProps {
  studySpec: StudySpec;
  baseUrl: string;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
}

export function EcoaVisitRouteInner(props: EcoaVisitRouteInnerProps) {
  const { studySpec, baseUrl, folderId, visitId, visitSeq, crfPageId, crfPageSeq } = props;
  const showFontController = !!studySpec.config?.ecoa?.settings?.showFontController;
  const defaultParams = { folderId, visitId, visitSeq, crfPageId, crfPageSeq };

  const ecoaNotice = studySpec.config?.ecoa?.notice;

  const isSkipNoticePage = (() => {
    if (!!ecoaNotice && !Array.isArray(ecoaNotice) && "skipPages" in ecoaNotice) {
      // skipPages에 정의된 페이지는 안내사항 페이지 skip
      return ecoaNotice.skipPages.some((page) => {
        const { folderId: fId, visitId: vId, visitSeq: vSeq, crfPageId: cpId, crfPageSeq: cpSeq } = page;

        return (
          ((fId && typeof fId === "object" && fId.reserved === "ANY") || fId === folderId) &&
          ((vId && typeof vId === "object" && vId.reserved === "ANY") || vId === visitId) &&
          ((vSeq && typeof vSeq === "object" && vSeq.reserved === "ANY") || vSeq === visitSeq) &&
          ((cpId && typeof cpId === "object" && cpId.reserved === "ANY") || cpId === crfPageId) &&
          ((cpSeq && typeof cpSeq === "object" && cpSeq.reserved === "ANY") || cpSeq === crfPageSeq || !cpSeq)
        );
      });
    } else if (!!ecoaNotice && !!Array.isArray(ecoaNotice)) {
      // target에 정의되어 있지 않은 페이지는 안내사항 페이지 skip
      return !ecoaNotice.some((notice) => {
        if ("target" in notice) {
          const { folderId: fId, visitId: vId, visitSeq: vSeq, crfPageId: cpId, crfPageSeq: cpSeq } = notice.target;

          return (
            ((fId && typeof fId === "object" && fId.reserved === "ANY") || fId === folderId) &&
            ((vId && typeof vId === "object" && vId.reserved === "ANY") || vId === visitId) &&
            ((vSeq && typeof vSeq === "object" && vSeq.reserved === "ANY") || vSeq === visitSeq) &&
            ((cpId && typeof cpId === "object" && cpId.reserved === "ANY") || cpId === crfPageId) &&
            ((cpSeq && typeof cpSeq === "object" && cpSeq.reserved === "ANY") || cpSeq === crfPageSeq || !cpSeq)
          );
        }

        return true;
      });
    } else {
      return false;
    }
  })();

  return (
    <FxGuard
      api={{ ...ecoaVisitApi.getEcoaVisitQuestionnaires(folderId, visitId, visitSeq, crfPageId, crfPageSeq), cacheMaxAge: 3600 }}
      render={(resp) => (
        <Switch>
          <Route
            path={`${baseUrl}/done`}
            component={() => <VisitQuestionnaireDone folderId={folderId} visitId={visitId} visitSeq={visitSeq} />}
          />
          <Route
            path={`${baseUrl}/:questionnaireSeq`}
            component={() => (
              <FxGuard
                api={{
                  ...ecoaCrfApi.getEcoaCrfs,
                  query: { folder_id: folderId, visit_id: visitId, visit_seq: visitSeq, crf_page_seq: crfPageSeq },
                }}
                render={(crfResp) => (
                  <CaptureContextProvider>
                    <VisitQuestionnaire
                      crfs={crfResp?.crfs!}
                      questionnairesData={{ ...resp?.questionnaire! }}
                      {...defaultParams}
                      studySpec={studySpec}
                    />
                  </CaptureContextProvider>
                )}
              />
            )}
          />
          {!isSkipNoticePage && (
            <Route
              path={`${baseUrl}`}
              component={() => (
                <VisitEntrance
                  studySpec={studySpec}
                  questionnaire={resp?.questionnaire!}
                  showFontController={showFontController}
                  noticeVerticalPosition={studySpec.config?.ecoa?.noticeVerticalPosition}
                  {...defaultParams}
                />
              )}
            />
          )}
          <Redirect from="*" to="/404?er" />
        </Switch>
      )}
    />
  );
}

export function EcoaVisitRoute({ studySpec }: EcoaVisitRouteProps) {
  const match = useRouteMatch();
  const params = useParams<{ folderId: string; visitId: string; visitSeq: string; crfPageId: string; crfPageSeq: string }>();
  const { folderId, visitId, visitSeq: visitSeqVal, crfPageId, crfPageSeq: crfPageSeqVal } = params;
  const visitSeq = useIntParam(visitSeqVal);
  const crfPageSeq = useIntParam(crfPageSeqVal);
  return (
    <EcoaVisitRouteInner
      studySpec={studySpec}
      baseUrl={match.url}
      folderId={folderId}
      visitId={visitId}
      visitSeq={visitSeq}
      crfPageId={crfPageId}
      crfPageSeq={crfPageSeq}
    />
  );
}
